import { ItemPriceState } from '../models/history-log';

/** Maps semantic group key → its member states. Used to encode/decode the ?state URL param. */
export const GROUP_STATES_MAP: Record<string, ItemPriceState[]> = {
  increase: [ItemPriceState.UNDERSTOCK_CRITICAL, ItemPriceState.UNDERSTOCK_MODERATE, ItemPriceState.UNDERSTOCK_MILD, ItemPriceState.STOCK_OUT_HIGH_DEMAND],
  decrease: [ItemPriceState.OVERSTOCK_HIGH, ItemPriceState.OVERSTOCK_CRITICAL, ItemPriceState.NO_DEMAND],
  maintain: [ItemPriceState.BALANCED, ItemPriceState.DEAD_STOCK, ItemPriceState.MISSING_PRICE, ItemPriceState.STOCK_OUT_RESTOCKING],
};

/**
 * Decode the ?state URL param into individual state values.
 * Expands group keys (e.g. "increase") to their member states.
 * Backward-compatible: raw state names (e.g. "UNDERSTOCK_CRITICAL") are passed through as-is.
 */
export function decodeStateParam(param: string): ItemPriceState[] {
  const states = new Set<ItemPriceState>();
  for (const token of param.split(',').filter(Boolean)) {
    const groupStates = GROUP_STATES_MAP[token];
    if (groupStates) {
      groupStates.forEach((s) => states.add(s));
    } else {
      states.add(token as ItemPriceState);
    }
  }
  return Array.from(states);
}

/**
 * Encode individual state values into a compact URL param.
 * Full groups are replaced by their group key (e.g. all 3 "increase" states → "increase").
 * Remaining individual states are appended as-is.
 */
export function encodeStateParam(states: string[]): string | null {
  const tokens: string[] = [];
  const remaining = new Set(states);
  for (const [key, groupStates] of Object.entries(GROUP_STATES_MAP)) {
    if (groupStates.every((s) => remaining.has(s))) {
      tokens.push(key);
      groupStates.forEach((s) => remaining.delete(s));
    }
  }
  remaining.forEach((s) => tokens.push(s));
  return tokens.sort().join(',') || null;
}
